export default {
  data() {
    return {
      speechAudio: null,
      audioContext: null,
      audioAnalyser: null,
      audioSource: null,
      audioDataArray: null,
      audioAnimationFrame: null,
      audioLevel: 0,
      isAudioAnalyzing: false,
    };
  },
  methods: {
    initAudioAnalyzer() {
      try {
        // Create AudioContext if it doesn't exist
        if (!this.audioContext) {
          const AudioContext = window.AudioContext || window.webkitAudioContext;
          this.audioContext = new AudioContext();
        }

        // Create AnalyserNode
        if (!this.audioAnalyser) {
          this.audioAnalyser = this.audioContext.createAnalyser();
          this.audioAnalyser.fftSize = 256; // Size of the FFT (Fast Fourier Transform)
          this.audioAnalyser.smoothingTimeConstant = 0.8; // Smoothing for audio data

          const bufferLength = this.audioAnalyser.frequencyBinCount;
          this.audioDataArray = new Uint8Array(bufferLength);
        }

        return true;
      } catch (error) {
        console.error("Error initializing audio analyzer:", error);
        return false;
      }
    },

    connectAudioElement(audioElement) {
      if (!audioElement) return false;

      try {
        // Initialize analyzer if needed
        if (!this.audioAnalyser && !this.initAudioAnalyzer()) {
          return false;
        }

        // Resume audio context if it's suspended (browser autoplay policy)
        if (this.audioContext.state === "suspended") {
          this.audioContext.resume();
        }

        // Create source from audio element
        if (!this.audioSource) {
          this.audioSource = this.audioContext.createMediaElementSource(audioElement);
          this.audioSource.connect(this.audioAnalyser);
          this.audioAnalyser.connect(this.audioContext.destination);
        }

        return true;
      } catch (error) {
        // If the source is already connected, just continue
        if (error.name === "InvalidStateError") {
          return true;
        }
        console.error("Error connecting audio element:", error);
        return false;
      }
    },

    startAudioAnalysis() {
      if (this.isAudioAnalyzing) return;

      this.isAudioAnalyzing = true;
      this.analyzeAudio();
    },

    analyzeAudio() {
      if (!this.isAudioAnalyzing) return;

      // Get frequency data
      this.audioAnalyser.getByteFrequencyData(this.audioDataArray);

      // Calculate average audio level
      let sum = 0;
      for (let i = 0; i < this.audioDataArray.length; i++) {
        sum += this.audioDataArray[i];
      }
      const average = sum / this.audioDataArray.length;

      // Normalize to 0-1 range (0-255 byte data)
      this.audioLevel = average / 255;

      // Call hook for components to use the data
      this.onAudioLevelUpdate(this.audioLevel, this.audioDataArray);

      // Continue analyzing
      this.audioAnimationFrame = requestAnimationFrame(() => this.analyzeAudio());
    },

    stopAudioAnalysis() {
      this.isAudioAnalyzing = false;

      if (this.audioAnimationFrame) {
        cancelAnimationFrame(this.audioAnimationFrame);
        this.audioAnimationFrame = null;
      }

      this.audioLevel = 0;
      this.onAudioLevelUpdate(0, null);
    },

    async playSpeechAudio(audioSrc) {
      try {
        return new Promise((resolve, reject) => {
          // Stop any existing audio
          this.stopSpeechAudio();

          // Create new audio element
          const audio = new Audio(audioSrc);

          audio.crossOrigin = "anonymous";

          // Set audio playing state when audio starts
          audio.onplay = () => {
            this.connectAudioElement(audio);
            this.startAudioAnalysis();
            this.onSpeechAudioPlay();
          };

          // Play the audio
          audio.play().catch((error) => {
            console.error("Error playing audio:", error);
            reject(error);
          });

          // Handle audio end
          audio.onended = () => {
            this.stopAudioAnalysis();
            this.onSpeechAudioStop({checkVoiceRecorder: true});
            resolve();
            this.speechAudio = null;
          };

          // Handle errors
          audio.onerror = (error) => {
            console.error(error);
            this.stopAudioAnalysis();
            this.onSpeechAudioStop();
            reject(error);
            this.speechAudio = null;
          };

          this.speechAudio = audio;
        });
      } catch (error) {
        console.error(error);
        this.onSpeechAudioStop();
        return null;
      }
    },

    stopSpeechAudio(options = {}) {
      if (this.speechAudio) {
        this.speechAudio.pause();
        this.speechAudio.currentTime = 0;
        this.speechAudio = null;
      }

      this.stopAudioAnalysis();
      this.onSpeechAudioStop(options);
      this.cleanupAudioSpeech();
    },

    cleanupAudioSpeech() {
      if (this.audioSource) {
        try {
          this.audioSource.disconnect();
        } catch (error) {
          // Ignore disconnect errors
        }
        this.audioSource = null;
      }

      if (this.audioContext && this.audioContext.state !== "closed") {
        this.audioContext.close();
        this.audioContext = null;
      }

      this.audioAnalyser = null;
      this.audioDataArray = null;
    },
    onAudioLevelUpdate(level, frequencyData) {},

    onSpeechAudioPlay() {},

    onSpeechAudioStop() {},
  },

  beforeUnmount() {
    this.stopSpeechAudio();
  },
};
